<template>
  <div class="solar-background" :class="{ 'is-paused': paused }" aria-hidden="true">
    <div class="solar-system">
      <div class="orbit-track orbit-track--inner"></div>
      <div class="orbit-track orbit-track--middle"></div>
      <div class="orbit-track orbit-track--outer"></div>

      <div class="sun">
        <div class="sun-glow"></div>
        <img class="sun-surface" src="@/assets/images/solar/blue-sun.webp" alt="" width="448" height="448" draggable="false">
      </div>

      <div class="orbit orbit--inner">
        <div class="planet-position">
          <img class="planet planet--mars" src="@/assets/images/solar/mars.webp" alt="" width="112" height="112" draggable="false">
        </div>
      </div>
      <div class="orbit orbit--middle">
        <div class="planet-position">
          <img class="planet planet--earth" src="@/assets/images/solar/earth.webp" alt="" width="128" height="128" draggable="false">
          <div class="moon-orbit">
            <img class="moon" src="@/assets/images/solar/moon.webp" alt="" width="48" height="48" draggable="false">
          </div>
        </div>
      </div>
      <div class="orbit orbit--outer">
        <div class="planet-position">
          <img class="planet planet--mercury" src="@/assets/images/solar/mercury.webp" alt="" width="96" height="96" draggable="false">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SolarBackground',
  data() {
    return { paused: document.hidden }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.updateVisibility)
    this.updateVisibility()
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.updateVisibility)
  },
  methods: {
    updateVisibility() {
      this.paused = document.hidden
    }
  }
}
</script>

<style scoped>
.solar-background {
  position: fixed;
  inset: 0 0 auto;
  /* Cover the viewport with browser controls hidden, including mid-scroll. */
  height: 100vh;
  height: 100lvh;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  background: linear-gradient(180deg, rgb(2 6 20 / 38%), rgb(2 6 20 / 64%)),
    #020614 url('../assets/images/solar/galaxy.webp') center / cover no-repeat;
}

.solar-system {
  position: absolute;
  left: 50%;
  top: 56%;
  width: min(78vw, 78vh, 760px);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
}

.sun {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 29%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
}

.sun-glow {
  position: absolute;
  inset: -68%;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(95 175 255 / 42%) 0%,
    rgb(45 108 255 / 20%) 30%, rgb(33 75 255 / 7%) 46%, transparent 68%);
}

.sun-surface {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.orbit-track {
  position: absolute;
  left: 50%;
  top: 50%;
  border: 1px solid rgb(141 190 255 / 11%);
  border-radius: 50%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
}

.orbit-track--inner { width: 51%; }
.orbit-track--middle { width: 75%; }
.orbit-track--outer { width: 100%; }

.orbit {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  transform: rotate(var(--phase));
}

.orbit--inner { --phase: 34deg; --period: 95s; --radius: 25.5; }
.orbit--middle { --phase: 207deg; --period: 155s; --radius: 37.5; }
.orbit--outer { --phase: 302deg; --period: 235s; --radius: 50; }

.planet-position {
  position: absolute;
  /* Match the scene width; moving these small sprites doesn't repaint a canvas. */
  left: min(calc(78vw * var(--radius) / 100), calc(78vh * var(--radius) / 100), calc(760px * var(--radius) / 100));
  top: 0;
}

.planet {
  display: block;
  position: absolute;
  width: clamp(22px, 4.5vmin, 42px);
  height: auto;
  max-width: none;
  transform: translate(-50%, -50%);
}

.planet--earth { width: clamp(29px, 5.8vmin, 55px); }
.planet--mercury { width: clamp(18px, 3.5vmin, 33px); }

.moon-orbit {
  position: absolute;
  width: 0;
  height: 0;
  --phase: 100deg;
  transform: rotate(var(--phase));
}

.moon {
  position: absolute;
  left: clamp(25px, 5vmin, 46px);
  top: 0;
  display: block;
  width: clamp(6px, 1.2vmin, 11px);
  height: auto;
  max-width: none;
  transform: translate(-50%, -50%);
}

@keyframes orbit {
  from { transform: rotate(var(--phase)); }
  to { transform: rotate(calc(var(--phase) + 360deg)); }
}

@keyframes sun-spin {
  to { transform: rotate(360deg); }
}

/* A composed static scene is the default, including on older browsers. */
@media (prefers-reduced-motion: no-preference) {
  .sun-surface { animation: sun-spin 180s linear infinite; }
  .orbit { animation: orbit var(--period) linear infinite; }
  .moon-orbit { animation: orbit 18s linear infinite; }
}

.is-paused .sun-surface,
.is-paused .orbit,
.is-paused .moon-orbit { animation-play-state: paused; }

@media (max-width: 600px) {
  .solar-system {
    top: 59%;
    width: min(86vw, 72vh);
  }

  .sun { width: 32%; }
  .planet-position {
    left: min(calc(86vw * var(--radius) / 100), calc(72vh * var(--radius) / 100));
  }

  .orbit--outer,
  .orbit-track--outer { display: none; }
}
</style>
